/* --------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

import _ from 'lodash';
import service from '../../services/recordings';

const RECORDINGS_ALL = 'RECORDINGS_ALL';
const RECORDINGS_SHARED = 'RECORDINGS_SHARED';
const RECORDINGS_ONE = 'RECORDINGS_ONE';
const RECORDINGS_DEL = 'RECORDINGS_DEL';
const RECORDINGS_POLL = 'RECORDINGS_POLL';
const RECORDINGS_POLLED = 'RECORDINGS_POLLED';
const RECORDINGS_RESET = 'RECORDINGS_RESET';
const RECORDINGS_SHARED_ONE = 'RECORDINGS_SHARED_ONE';

const state = {
  list: [],
  sharedList: [],
  polledIds: []
};

const mutations = {
  [RECORDINGS_ALL] (state, recordings) {
    const byId = _.keyBy(state.list, r => r.recordingId);
    state.list = _.map(recordings, recording => {
      return _.merge(recording, byId[recording.recordingId] || {});
    });
  },

  [RECORDINGS_SHARED] (state, recordings) {
    const byId = _.keyBy(state.sharedList, r => r.recordingId);
    state.sharedList = _.map(recordings, recording => {
      return _.merge(recording, byId[recording.recordingId] || {});
    });
  },

  [RECORDINGS_ONE] (state, recordingData) {
    const recording = _.find(state.list, r => r.recordingId === recordingData.recordingId);
    if (recording) {
      _.assign(recording, recordingData);
    } else {
      state.list.push(recordingData);
    }
  },

  [RECORDINGS_SHARED_ONE] (state, recordingData) {
    const recording = _.find(state.sharedList, r => r.recordingId === recordingData.recordingId);
    if (recording) {
      _.assign(recording, recordingData);
    } else {
      state.sharedList.push(recordingData);
    }
  },

  [RECORDINGS_DEL] (state, recordingId) {
    // not using _.pull so Vue detects the change
    const index = _.findIndex(state.list, r => r.recordingId === recordingId);
    if (index >= 0) {
      state.list.splice(index, 1);
    }
  },

  [RECORDINGS_POLL] (state, recordingIds) {
    state.polledIds = _.union(state.polledIds, recordingIds);
  },

  [RECORDINGS_POLLED] (state) {
    state.polledIds = [];
  },

  [RECORDINGS_RESET] (state) {
    state.list = [];
    state.polledIds = [];
  }
};

const actions = {
  listRecordings: ({ commit }) => {
    return service.list()
      .then(recordings => {
        commit(RECORDINGS_ALL, recordings);
        const idsToPoll = _.map(_.filter(recordings, r => r.processing || (r.isVideo && !r.videoPlaybackS3Path)), r => r.recordingId);
        if (idsToPoll.length) {
          commit(RECORDINGS_POLL, idsToPoll);
        }
      })
      .catch(() => {}); // this will show an alert automatically
  },
  getRecording: ({ commit, dispatch, rootState }, {recordingId}) => {
    return service.get(recordingId)
      .then(recording => {
        // Save the scorecard responses
        if (recording.assignment && recording.assignment.scorecard &&
          recording.assignment.scorecard.responses && recording.assignment.scorecard.responses.length) {
          dispatch(
            'registerRecordingResponses',
            recording.assignment.scorecard.responses
          );
        }

        const isShared = !!(recording.userId !== rootState.user.user.userId);
        if (!isShared) {
          commit(RECORDINGS_ONE, recording);
          if (recording.processing || (recording.isVideo && !recording.videoPlaybackS3Path)) {
            commit(RECORDINGS_POLL, [recordingId]);
          }
        } else {
          commit(RECORDINGS_SHARED_ONE, recording);
        }
      });
  },
  pollRecordings: ({ commit, dispatch, state }) => {
    _.forEach(state.polledIds, recordingId => {
      dispatch('getRecording', {recordingId});
    });
    commit(RECORDINGS_POLLED);
  },
  delRecording: ({ commit }, recordingId) => {
    return service.del(recordingId)
      .then(() => {
        commit(RECORDINGS_DEL, recordingId);
      });
  },
  updateRecording: ({ commit }, recordingData) => {
    return service.put(recordingData.recordingId, recordingData)
      .then(() => {
        if (recordingData.submitted) {
          recordingData.submissionDate = new Date();
        }
        if (recordingData.feedbackRequested) {
          recordingData.feedbackRequestedDate = new Date();
        }
        commit(RECORDINGS_ONE, recordingData);
      });
  },
  updateSpeakerNum: ({ commit }, recordingData) => {
    const postData = { speakerNum: recordingData.speakerNum };
    return service.updateSpeakerNum(recordingData.recordingId, postData)
      .then(() => {
        commit(RECORDINGS_ONE, recordingData);
      });
  },
  resetRecordings: ({ commit }) => {
    commit(RECORDINGS_RESET);
  }
};

const getters = {
  recordings: state => state.list,
  singleSpeakerRecordings: (state) => {
    return _.filter(state.list, (rec) => {
      return (!rec.results || !rec.results.scores || !rec.results.scores.perSpeaker);
    });
  },
  sharedRecordings: state => state.sharedList
};

export default {
  state,
  mutations,
  actions,
  getters
};
